import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMemberTeams, useTeams } from '../use-teams';
import { saveContacts } from './client-contacts';
import { useSaveToasts } from '../use-save-toasts';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGoBack, useWorkspaceNavigate } from '../../workspace-path';
import {
  clientSchema,
  projectMemberSchema,
  projectSchema,
  projectViewCountsSchema,
  type ProjectViewCounts,
  lineItemHasWork,
  lineItemSchema,
  type LineItem,
  taskPrioritySchema,
  taskSchema,
  taskStatusSchema,
  metafieldSchema,
  projectRoleSchema,
  workspaceMemberSchema,
  worklogSchema,
  type Client,
  type Metafield,
  type ProjectRole,
  type Project,
  type ProjectMember,
  type Task,
  type TaskPriority,
  type TaskStatus,
  type Worklog,
  type WorkspaceMember,
  formatSort,
  type SortKey,
} from '@forecastify/shared';
import { Toaster, nextSurface, type SortState, type Toast } from '@forecastify/ui';
import { usePages } from '../use-pages';
import { useT, type Translate } from '../../i18n';
import { parseList, totalOf, usePermissionSet } from '../permissions-hook';
import { useListQuery } from '../use-list-query';
import { applyGridEdit } from '../tasks/grid-edit';
import type { TaskFilters } from '../tasks/tasks-screen';
import { setTrackerParams, useTrackerValues } from '../tasks/tracker-filters';
import { bulkTargets, postBulk } from '../use-bulk';
import { useFirstLoad } from '../use-first-load';
import { useLiveInvalidation } from '../../live/use-live';
import { DELIVERY_TOPICS, PROJECT_DETAIL_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { ClientsScreen } from './clients-screen';
import type { ClientDraft } from './client-dialog';
import { ProjectScreen } from './project-screen';
import { ProjectsScreen } from './projects-screen';
import type { TaskDraft } from '../tasks/task-dialog';
import { TaskOverlay } from '../tasks/task-overlay';
import type { TaskMode } from '../tasks/task-surface';
import { useExport } from '../exports/use-export';
import { useImport } from '../imports/use-import';
import { lineItemContractLabel } from '../contracts/contract-label';
import { readAll, requestAll } from '../read-all';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The three delivery routes, and the fetching all three share.              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Same split as every other screen in this app: the screen renders props, the route fetches. And
 * every response is **parsed with the shared schema rather than cast** — `as Client[]` on a `fetch`
 * result is a lie the compiler believes, and the first symptom is `undefined` inside a render
 * rather than one legible failure at the boundary.
 *
 * ⚠️ **Every load has three outcomes and none of them is a silent `return`.** `hard-won.md` C6-9
 * and C6-11 are both spinners that never stopped: one because `fetch` *rejects* when the API is
 * unreachable rather than resolving with `ok: false`, the other because a bare `if (!response.ok)
 * return;` left the screen in `loading` for ever. Both shapes are handled explicitly below.
 */

/* ── Ügyfelek ──────────────────────────────────────────────────────────────────────────────── */

export function ClientsRoute() {
  const session = useSession();
  const t = useT();
  const permissions = usePermissionSet();
  const [clients, setClients] = useState<readonly Client[]>([]);

  /**
   * The workspace's metafield definitions for this entity.
   *
   * ⚠️ **Not fatal.** A caller who cannot read them gets the form without the custom-field box
   * rather than no form at all — the same rule every picker feed on this route follows.
   */
  const [metafields, setMetafields] = useState<readonly Metafield[]>([]);
  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await session.request('/metafields?entity=client').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(metafieldSchema, await response.json());
      if (parsed !== null) setMetafields(parsed);
    })();
  }, [session]);
  /* ⚠️ Opens on the live clients, which is the state the screen has always defaulted to — the
     archived ones are asked for rather than hidden (`clients-screen.tsx`'s own note). */
  const query = useListQuery({ state: 'live' });
  const { pages, params } = query;
  const { settle: settlePage } = pages;
  const [industries, setIndustries] = useState<readonly string[]>([]);
  /* ⚠️ The bulk bar's own reports — this screen had none before item 72. */
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    /* ⚠️ The list screen pages, so it asks for the contract's default rather than the ceiling.
       ⚠️ Search, filters, sort and offset all travel — item 35 moved every one of them here from
       the screen's own `useState`, where they filtered a page rather than a list. */
    const text = params.toString();
    const response = await session
      .request(`/clients${text === '' ? '' : `?${text}`}`)
      .catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const body: unknown = await response.json();
    const parsed = parseList(clientSchema, body);
    if (parsed === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    setFailure(null);
    setClients(parsed);
    settlePage(totalOf(body));

    /* ⚠️ **Loaded separately and its failure is NOT fatal**, like the project screen's two picker
       feeds: an empty industry filter is a smaller loss than a screen that will not render. */
    const list = await session.request('/clients/industries').catch(() => null);
    if (list?.ok === true) {
      const payload: unknown = await list.json();
      const names = (payload as { data?: unknown }).data;
      if (Array.isArray(names)) {
        setIndustries(names.filter((name): name is string => typeof name === 'string'));
      }
    }
  }, [session, params, settlePage]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. A colleague's change
   * refetches this in place — no spinner, no blank, no reload. R-19. */
  useLiveInvalidation(DELIVERY_TOPICS, reload);

  /**
   * ⚠️ **A refusal from a WRITE is not a screen failure.** `ScreenState` replaces the whole screen;
   * a duplicate code or a client that still has projects has to appear **in the dialog**, beside
   * the field somebody would change. Losing the form to a full-page error would lose the draft too.
   */
  /** 26 Oct 2026 · resolves to whether it landed — the dialog closes on `true` only. */
  const write = async (
    path: string,
    init: RequestInit,
    onDone: (body: unknown) => void | Promise<void>,
  ): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      setServerError(await messageFor(response, t));
      return false;
    }
    await onDone(await response.json().catch(() => null));
    await reload();
    return true;
  };

  /* 26 Oct 2026 · a save that landed says so — the dialog closing alone read as nothing. */
  const saved = (title: string): void => {
    setToasts((current) => [
      ...current,
      { id: `client-saved-${String(Date.now())}`, title, variant: 'success' },
    ]);
  };

  /** 21 Oct 2026 · after the client is saved, its contacts — they need its id to hang on. */
  const writeContacts = async (clientId: string, draft: ClientDraft): Promise<void> => {
    const refused = await saveContacts(session, t, clientId, draft.contactsBefore, draft.contacts);
    if (refused !== null) setServerError(t('clients.contacts.saveFailed', { reason: refused }));
  };

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <ClientsScreen
        loading={loading}
        page={{
          hasPrevious: pages.hasPrevious,
          hasNext: pages.hasNext,
          onPrevious: pages.previous,
          onNext: pages.next,
          /* ⚠️ Item 20's `[<] [selector] [>] [total]`. The total counts what the filters
             left, which is what `list.pageTotal` says in words. */
          index: pages.index,
          count: pages.count,
          total: pages.total,
          onGoTo: pages.goTo,
        }}
        query={query}
        industries={industries}
        clients={clients}
        metafields={metafields}
        canManage={permissions.has('client.manage')}
        serverError={serverError}
        onCreate={async (draft) => {
          const landed = await write(
            '/clients',
            { method: 'POST', body: JSON.stringify(bodyOf(draft)) },
            async (body) => {
              const created = clientSchema.safeParse(body);
              if (created.success) await writeContacts(created.data.id, draft);
            },
          );
          if (landed) saved(t('clients.toast.created', { name: draft.name.trim() }));
          return landed;
        }}
        onUpdate={async (client, draft) => {
          const landed = await write(
            `/clients/${client.id}`,
            { method: 'PUT', body: JSON.stringify({ ...bodyOf(draft), version: client.version }) },
            () => writeContacts(client.id, draft),
          );
          if (landed) saved(t('clients.toast.updated', { name: draft.name.trim() }));
          return landed;
        }}
        onArchive={(client) => {
          void write(
            `/clients/${client.id}/archive`,
            {
              method: 'PATCH',
              body: JSON.stringify({
                archived: client.archivedAt === null,
                version: client.version,
              }),
            },
            () => undefined,
          );
        }}
        /**
         * ⚠️ **Item 72 · archive or restore a selection.** One request; the version travels with
         * each id so a client somebody else has touched is refused rather than overwritten, and
         * the toast counts what landed.
         */
        {...(permissions.has('client.manage')
          ? {
              onArchiveMany: (ids: readonly string[], archived: boolean) => {
                void (async () => {
                  const result = await postBulk(session, '/clients/bulk-archive', {
                    archived,
                    targets: bulkTargets(clients, ids),
                  });
                  await reload();
                  setToasts((current) => [
                    ...current,
                    {
                      id: `bulk-archive-${String(current.length)}`,
                      title:
                        result === null
                          ? t('error.unavailable')
                          : t('clients.toast.bulkArchived', { count: result.done.length }),
                      ...(result === null || result.failed.length === 0
                        ? {}
                        : { description: t('bulk.someRefused', { total: ids.length }) }),
                      variant: result !== null && result.failed.length === 0 ? 'success' : 'error',
                    },
                  ]);
                })();
              },
            }
          : {})}
      />

      {/* ⚠️ One `Toaster`, and the bulk bar is its only source on this screen. */}
      <Toaster
        toasts={[...toasts]}
        label={t('clients.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
      />
    </ScreenState>
  );
}

/** Empty optional strings become `null`, which is what "clear this" means on the wire. */
function bodyOf(draft: {
  name: string;
  contactName: string;
  contactEmail: string;
  countryCode: string;
  industry: string;
  color: string;
  metafields: Record<string, unknown>;
}) {
  return {
    name: draft.name.trim(),
    contactName: draft.contactName.trim() === '' ? null : draft.contactName.trim(),
    contactEmail: draft.contactEmail.trim() === '' ? null : draft.contactEmail.trim(),
    countryCode: draft.countryCode,
    industry: draft.industry.trim(),
    color: draft.color,
    metafields: draft.metafields,
  };
}

/* ── Projektek ─────────────────────────────────────────────────────────────────────────────── */

/**
 * 26 Oct 2026 · the project dialog's quick *Új ügyfél*, shared by the projects list (create) and
 * the project page (edit) — the page never passed it, so editing a project offered no new client.
 * Resolves to the new client, or `null` when the create was refused.
 */
async function createClientFrom(
  session: ReturnType<typeof useSession>,
  t: Translate,
  draft: ClientDraft,
): Promise<Client | null> {
  const response = await session
    .request('/clients', { method: 'POST', body: JSON.stringify(bodyOf(draft)) })
    .catch(() => null);
  if (response?.ok !== true) return null;
  /* ⚠️ **A single resource comes back bare, not in `{ data }`** — `parseOne` read `.data`, found
     nothing, and the dialog reported a refusal for a client it had just created, so the second
     Mentés made a duplicate. */
  const parsed = clientSchema.safeParse(await response.json());
  if (!parsed.success) return null;
  /* 21 Oct 2026 · the quick form's contacts, once the client exists. */
  await saveContacts(session, t, parsed.data.id, [], draft.contacts);
  return parsed.data;
}

/** The workspace's client metafields, for the quick client form — empty until they land. */
function useClientMetafields(session: ReturnType<typeof useSession>): readonly Metafield[] {
  const [fields, setFields] = useState<readonly Metafield[]>([]);
  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await session.request('/metafields?entity=client').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(metafieldSchema, await response.json());
      if (parsed !== null) setFields(parsed);
    })();
  }, [session]);
  return fields;
}

export function ProjectsRoute({ mine = false }: { readonly mine?: boolean } = {}) {
  const session = useSession();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  /* The sidebar's `+` lands here as `?new=1` — see where it is passed down. */
  const [params, setParams] = useSearchParams();
  const t = useT();
  const permissions = usePermissionSet();
  const [projects, setProjects] = useState<readonly Project[]>([]);
  const [clients, setClients] = useState<readonly Client[]>([]);

  /** The workspace's `project` metafields. Not fatal — see the clients route's own note. */
  const [metafields, setMetafields] = useState<readonly Metafield[]>([]);
  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await session.request('/metafields?entity=project').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(metafieldSchema, await response.json());
      if (parsed !== null) setMetafields(parsed);
    })();
  }, [session]);
  /** The workspace's `client` metafields — the client form the project dialog opens asks them. */
  const clientMetafields = useClientMetafields(session);
  const [members, setMembers] = useState<readonly WorkspaceMember[]>([]);
  /* ⚠️ `view` is the SCREEN's — it is three filters wearing one control, and the screen sets those
     alongside it. `use-list-query.ts`'s `local` parameter carries the argument. */
  /* IP-4 · `archived: 'hide'` from the first paint — an archived project is listed only under its
     own view, which clears it. */
  const query = useListQuery({ view: 'all', archived: 'hide' }, ['view']);
  const { pages, params: listParams } = query;
  const { settle: settlePage } = pages;
  const [viewCounts, setViewCounts] = useState<ProjectViewCounts | undefined>(undefined);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  /* ⚠️ T-10's shared path. This screen holds no CSV writer — see `use-export.tsx`. */
  const exporter = useExport();

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    /* ⚠️ The list screen pages, so it asks for the contract's default rather than the ceiling.
       ⚠️ Search, filters, sort and offset all travel — item 35 moved every one of them here from
       the screen's own `useState`, where they filtered a page rather than a list. */
    const text = listParams.toString();
    /* ⚠️ The three non-fatal feeds below start NOW, beside the list, rather than after it: the
       first load's skeleton waits for this whole function, and it waited for four round trips in a
       row (13 Oct 2026). Each is still read, and still forgiven, where it was. */
    const clientRequest = requestAll(session, '/clients').catch(() => null);
    const countRequest = session.request('/projects/view-counts').catch(() => null);
    const memberRequest = requestAll(session, '/members').catch(() => null);
    const response = await session
      .request(`/projects${text === '' ? '' : `?${text}`}`)
      .catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const body: unknown = await response.json();
    const parsed = parseList(projectSchema, body);
    if (parsed === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    setFailure(null);
    setProjects(parsed);
    settlePage(totalOf(body));

    /**
     * ⚠️ **The two picker feeds are loaded SEPARATELY and their failure is not fatal.** A caller
     * without `client.view` (R-46) gets a 404 from `/clients` — rule 5 — and the project list still
     * works; their client filter is simply empty. Folding these into the guard above would make a
     * permission they do not need decide whether they see any projects at all.
     */
    const clientResponse = await clientRequest;
    if (clientResponse?.ok === true) {
      const list = parseList(clientSchema, await clientResponse.json());
      if (list !== null) setClients(list);
    }

    /* ⚠️ **The four view counts, from the server** — counted here they would be *"how many on this
       page"* wearing the label *"how many there are"* (`state-rules.md` §5). Not fatal. */
    const counts = await countRequest;
    if (counts?.ok === true) {
      const payload = (await counts.json()) as { data?: unknown };
      const parsedCounts = projectViewCountsSchema.safeParse(payload.data);
      if (parsedCounts.success) setViewCounts(parsedCounts.data);
    }

    const memberResponse = await memberRequest;
    if (memberResponse?.ok === true) {
      const list = parseList(workspaceMemberSchema, await memberResponse.json());
      if (list !== null) setMembers(list);
    }

    /* ⚠️ `settlePage` is React's own setter and stable; `pages` is not, and depending on it would
       make this callback change every time it settled — an infinite reload. */
  }, [session, listParams, settlePage]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. R-19. */
  useLiveInvalidation(DELIVERY_TOPICS, reload);

  /* ⚠️ **The archive loop that used this is gone** — item 72 gave it a bulk endpoint, and the
     create and update paths below each carry their own request because both need the created row
     back for the linking step. */

  /** Archive, or (30 Oct 2026) un-archive, the selection — one bulk request either way. */
  const archive = async (selection: readonly Project[], archived: boolean): Promise<void> => {
    /**
     * ⚠️⚠️ **ONE REQUEST AND ONE COLUMN, WHERE THIS WAS N FULL REPLACES.** It rebuilt every
     * field of every project and sent a `PUT` each, because `updateProjectRequestSchema` is
     * a replace and `status` had no path of its own — which meant archiving eight projects
     * rewrote eight names, eight colours and eight date pairs, and lost whatever a
     * colleague had changed in between. `ProjectService.setStatus` moves one column;
     * `bulk.ts` reports per row.
     */
    if (selection.length === 0) return;
    const result = await postBulk(session, '/projects/bulk-archive', {
      archived,
      targets: bulkTargets(
        projects,
        selection.map((project) => project.id),
      ),
    });
    await reload();
    if (result === null) {
      setToasts((current) => [
        ...current,
        {
          id: `archive-fail-${String(current.length)}`,
          title: t('error.unavailable'),
          variant: 'error',
        },
      ]);
      return;
    }
    setToasts((current) => [
      ...current,
      {
        id: `archive-${selection[0]?.id ?? ''}-${String(current.length)}`,
        title:
          result.done.length === 1
            ? t(archived ? 'projects.toast.archived' : 'projects.toast.unarchived')
            : t(archived ? 'projects.toast.archivedMany' : 'projects.toast.unarchivedMany', {
                count: result.done.length,
              }),
        /* ⚠️ The names of what LANDED, not of what was selected — `state-rules.md` §5. */
        description: selection
          .filter((project) => result.done.includes(project.id))
          .map((project) => project.name)
          .join(', '),
        variant: result.failed.length === 0 ? 'success' : 'error',
      },
    ]);
  };

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <ProjectsScreen
        loading={loading}
        mine={mine}
        page={{
          hasPrevious: pages.hasPrevious,
          hasNext: pages.hasNext,
          onPrevious: pages.previous,
          onNext: pages.next,
          /* ⚠️ Item 20's `[<] [selector] [>] [total]`. The total counts what the filters
             left, which is what `list.pageTotal` says in words. */
          index: pages.index,
          count: pages.count,
          total: pages.total,
          onGoTo: pages.goTo,
        }}
        query={query}
        viewCounts={viewCounts}
        /**
         * ⚠️ **Item 21 · a client created from inside the project form.** The id comes back so the
         * picker can select it; `null` says the create was refused and leaves the quick dialog
         * open with what was typed.
         *
         * ⚠️ **The server fills in the rest.** `createClientRequestSchema` needs a country and an
         * industry — the quick form asks for a NAME, so those are the sensible defaults a person
         * would have typed anyway, and the full dialog is where they are corrected.
         */
        clientMetafields={clientMetafields}
        /* ⚠️ Offered only to somebody who may create a client (R-46) — otherwise the option is
           a door onto a refusal. */
        {...(permissions.has('client.manage')
          ? {
              onCreateClient: async (draft: ClientDraft) => {
                const created = await createClientFrom(session, t, draft);
                if (created === null) return null;
                await reload();
                return created.id;
              },
            }
          : {})}
        /**
         * ⚠️ **`?new=1` is read HERE, where router context belongs.** The sidebar's `+` cannot
         * navigate to a dialog, so it navigates to this address with a flag; the route turns that
         * into a prop and clears it, and the screen stays presentational.
         *
         * ⚠️ **Cleared with `replace`**, or the parameter re-opens the dialog on every Back — and
         * puts it back on screen after somebody cancelled it.
         */
        createRequested={params.get('new') !== null}
        onCreateConsumed={() => {
          const next = new URLSearchParams(params);
          next.delete('new');
          setParams(next, { replace: true });
        }}
        projects={projects}
        clients={clients}
        members={members.map((member) => ({ id: member.id, name: member.displayName }))}
        /* ⚠️ The member id is per-WORKSPACE — `session.workspaces` carries one each. */
        currentMemberId={
          session.workspaces.find((entry) => entry.id === session.workspaceId)?.memberId ?? null
        }
        canCreate={permissions.has('project.create')}
        canManage={permissions.has('project.manage')}
        serverError={serverError}
        metafields={metafields}
        onOpen={(project) => {
          go(`/projects/${project.id}`);
        }}
        /* ⚠️ 21 Oct 2026 · both answer whether they landed — the dialog closes on `true` only. */
        onCreate={async (draft) => {
          setServerError(null);
          const created = await session
            .request('/projects', {
              method: 'POST',
              body: JSON.stringify(projectBody(draft)),
            })
            .catch(() => null);
          if (created?.ok !== true) {
            setServerError(
              created === null ? t('error.unavailable') : await messageFor(created, t),
            );
            return false;
          }
          await reload();
          return true;
        }}
        onUpdate={async (project, draft) => {
          setServerError(null);
          const { keyPrefix: _prefix, ...rest } = projectBody(draft);
          const saved = await session
            .request(`/projects/${project.id}`, {
              method: 'PUT',
              body: JSON.stringify({ ...rest, version: project.version }),
            })
            .catch(() => null);
          if (saved?.ok !== true) {
            setServerError(saved === null ? t('error.unavailable') : await messageFor(saved, t));
            return false;
          }
          await reload();
          return true;
        }}
        onFilterTasks={(project) => {
          go(`/tasks?projectId=${project.id}`);
        }}
        onTimeline={(project) => {
          go(`/tasks?projectId=${project.id}&view=timeline`);
        }}
        onReport={(project) => {
          go(`/reports/new?projectId=${project.id}`);
        }}
        onExport={(selection) => {
          exporter.open('project', { ids: selection.map((project) => project.id) });
        }}
        /**
         * ⚠️⚠️ **ARCHIVING IS THE ORDINARY UPDATE, ONE REQUEST PER PROJECT, AND IN SEQUENCE.**
         * There is no bulk endpoint, and firing N concurrent `PUT`s would race `reload()` against
         * itself and leave the list showing whichever finished last. Each write carries its own
         * `version`, so a stale row fails its own request rather than the batch.
         *
         * ⚠️ **`await`ing `write` per project reloads N times**, which is wasteful and correct —
         * the alternative is a private path that skips the reload, and a second write path for one
         * column is how the two come to disagree.
         */
        onUnarchive={(selection) => {
          void archive(selection, false);
        }}
        onArchive={(selection) => {
          void archive(selection, true);
        }}
      />

      {exporter.dialog}

      {/* ⚠️ One `Toaster`, two sources — two in the same corner hide each other. */}
      <Toaster
        toasts={[...toasts, ...exporter.toasts]}
        label={t('projects.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
          exporter.dismiss(id);
        }}
      />
    </ScreenState>
  );
}

function projectBody(draft: {
  keyPrefix: string;
  name: string;
  clientId: string;
  status: string;
  pmMemberId: string;
  code: string;
  startsOn: string;
  endsOn: string;
  color: string;
  metafields: Record<string, unknown>;
}) {
  return {
    keyPrefix: draft.keyPrefix,
    name: draft.name.trim(),
    clientId: draft.clientId === '' ? null : draft.clientId,
    status: draft.status,
    pmMemberId: draft.pmMemberId === '' ? null : draft.pmMemberId,
    code: draft.code.trim() === '' ? null : draft.code.trim(),
    startsOn: draft.startsOn === '' ? null : draft.startsOn,
    endsOn: draft.endsOn === '' ? null : draft.endsOn,
    color: draft.color,
    metafields: draft.metafields,
  };
}

/* ── one project ───────────────────────────────────────────────────────────────────────────── */

export function ProjectRoute() {
  const session = useSession();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  const goBack = useGoBack();
  const params = useParams();
  const t = useT();
  /* 26 Oct 2026 · the add-member dialog's success toast. */
  const { saved, toaster } = useSaveToasts();
  const permissions = usePermissionSet();
  /* ⚠️ Who is reading, from the session's workspace row — `tasks-route.tsx` argues the source. */
  const myMemberId =
    session.workspaces.find((workspace) => workspace.id === session.workspaceId)?.memberId ?? '';
  /**
   * C31 · the project-role vocabulary, for the People tab's picker and its column.
   *
   * ⚠️ **Non-fatal, and an empty list HIDES the picker** rather than showing an empty dropdown —
   * a workspace that has deleted all seven should not be offered a control with nothing in it.
   */
  const [projectRoles, setProjectRoles] = useState<readonly ProjectRole[]>([]);
  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await session.request('/project-roles').catch(() => null);
      if (response?.ok !== true) return;
      setProjectRoles(parseList(projectRoleSchema, await response.json()) ?? []);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it refetches for ever. */
  }, [session.workspaceId]);

  const projectId = params.id ?? '';

  const [project, setProject] = useState<Project | null>(null);
  const [members, setMembers] = useState<readonly ProjectMember[]>([]);
  const [candidates, setCandidates] = useState<readonly WorkspaceMember[]>([]);
  /* The task open over this project, and which frame it wears. See `task-overlay.tsx`. */
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  /* ⚠️ Item 34's ladder — a drill-in from a list opens as a modal. `nextSurface` carries it. */
  const [taskMode, setTaskMode] = useState<TaskMode>(nextSurface('page'));

  const [clients, setClients] = useState<readonly Client[]>([]);
  const clientMetafields = useClientMetafields(session);

  /**
   * The workspace's `project` metafields, for the edit dialog this page opens.
   *
   * ⚠️⚠️ **THE DETAIL PAGE HAS ITS OWN `ProjectDialog` AND IT WAS NOT GIVEN THESE.** The list's
   * dialog got them and this one did not, so *Projekt szerkesztése* from the header kebab — which
   * is where the mockup puts editing, and the way most edits actually start — opened a form with no
   * custom-field box. The prop defaults to empty, so nothing failed and nothing rendered.
   */
  const [metafields, setMetafields] = useState<readonly Metafield[]>([]);
  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await session.request('/metafields?entity=project').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(metafieldSchema, await response.json());
      if (parsed !== null) setMetafields(parsed);
    })();
  }, [session]);
  /** The workspace's `task` metafields — the Feladatok tab's columns and filters, as on `/tasks`. */
  const [taskMetafields, setTaskMetafields] = useState<readonly Metafield[]>([]);
  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await session.request('/metafields?entity=task').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(metafieldSchema, await response.json());
      if (parsed !== null) setTaskMetafields(parsed);
    })();
  }, [session]);
  /* C8. The project's own tasks, and the two vocabularies needed to render and create one. */
  const [tasks, setTasks] = useState<readonly Task[]>([]);
  /**
   * ⚠️⚠️ **C25's PICKER NEEDED A LIST AND THIS ROUTE HAD NONE.** The task DETAIL has offered a
   * line item since C25 because `task-route.tsx` fetches them; the new-task dialog never could,
   * so the only way to attach one was to create the task and then open it. Narrowed to this
   * project below — a line item reaches a project through its contract, and offering another
   * project's would earn a `line_item_not_on_project` refusal nobody should have to see.
   */
  const [lineItems, setLineItems] = useState<readonly LineItem[]>([]);
  /**
   * ⚠️⚠️ **THE FELADATOK TAB PAGES, AND UNTIL NOW IT SILENTLY DID NOT.** The API bounds this
   * list at fifty, so the tab showed fifty of a project's eight hundred tasks with no control to
   * reach the rest and a footer confidently calling it *50 tasks*. The same hook the tasks LIST uses,
   * so Previous and Next behave identically in both places.
   */
  const taskPages = usePages();
  const [statuses, setStatuses] = useState<readonly TaskStatus[]>([]);
  /**
   * ⚠️ **Item 30 · the Feladatok tab's four views need a filter state.** Pinned to this project by
   * the tab itself, so nothing here can widen it — the project id is not a filter somebody sets on
   * a project's own page.
   */
  /* ⚠️ T-10's shared paths, for the tab's own export and import — the same two the global tasks
     screen uses, so a CSV taken from a project reads identically to one taken from `/tasks`. */
  const exporter = useExport();
  const importer = useImport(() => void reload());
  /* ⚠️ The Feladatok tab's sort. It was never held here, so the header arrows changed nothing:
     `DataList` reports a sort and leaves the ordering to whoever asks the server. */
  const [taskSort, setTaskSort] = useState<readonly SortKey[]>([]);
  const [taskFilters, setTaskFilters] = useState<TaskFilters>({
    search: '',
    projectId: '',
    statusId: '',
    priorityId: '',
    assigneeMemberId: '',
    teamId: '',
    dueRange: '',
    estimateRange: '',
    metafields: {},
    component: '',
    fixVersion: '',
    epic: '',
  });
  /* J-1 · the tracker values this project's synced tasks carry, re-read with the tasks. */
  const taskTrackerValues = useTrackerValues(projectId, tasks);
  const [priorities, setPriorities] = useState<readonly TaskPriority[]>([]);
  /* C11. The project's worklogs. */
  const [worklogs, setWorklogs] = useState<readonly Worklog[]>([]);
  /**
   * The Idő tab's paging state.
   *
   * ⚠️⚠️ **OFFSET PAGES, 30 Oct 2026 — NOT A CURSOR AND *LOAD MORE*.** It loaded fifty with a
   * cursor, the table paged those fifty in the browser, and *Load more* appended the next: two
   * pagers on one list. The product owner: *"a pagination should be enough."* Every other list
   * pages on the server by offset (`state-rules.md` §6, as amended), and now this one does.
   */
  const logPages = usePages();
  const [logSort, setLogSort] = useState<readonly SortState[]>([]);
  const [logSearch, setLogSearch] = useState('');
  const [logMemberId, setLogMemberId] = useState('');
  /* R-60 · the Idő tab's team filter. */
  const teams = useTeams();
  const memberTeams = useMemberTeams();
  const [logTeamId, setLogTeamId] = useState('');
  /* ⚠️ Round 5 item 13 — both ranges are one `from..to` string, split where they become query
     parameters and nowhere else. `project-time-tab.tsx` argues the shape. */
  const [logDateRange, setLogDateRange] = useState('');
  const [logTaskId, setLogTaskId] = useState('');
  /* 30 Oct 2026 · the time tab's task filter options — every task on the project, read once. */
  const [logTaskChoices, setLogTaskChoices] = useState<
    readonly { id: string; key: string; title: string }[]
  >([]);
  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const rows = await readAll(
        session,
        `/tasks?projectId=${encodeURIComponent(projectId)}`,
        taskSchema,
      ).catch(() => null);
      setLogTaskChoices(
        (rows ?? []).map((task) => ({ id: task.id, key: task.key, title: task.title })),
      );
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug). */
  }, [session.workspaceId, projectId]);
  const [logHoursRange, setLogHoursRange] = useState('');
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  /**
   * ⚠️ **Built once and shared by the first page and every later one**, so "load more" cannot drift
   * from the filters that produced the page it is extending — which is how the second page of a
   * search ends up being an unfiltered page.
   */
  const logQuery = useMemo(() => {
    const params = new URLSearchParams({ projectId });
    /* 30 Oct 2026 · the page and the order travel — the server pages and sorts. */
    if (logPages.offset > 0) params.set('offset', String(logPages.offset));
    const logOrder = formatSort(logSort);
    if (logOrder !== undefined) params.set('sort', logOrder);
    if (logSearch.trim() !== '') params.set('search', logSearch.trim());
    if (logMemberId !== '') params.set('memberId', logMemberId);
    if (logTeamId !== '') params.set('teamId', logTeamId);
    if (logTaskId !== '') params.set('taskId', logTaskId);
    /**
     * ⚠️⚠️ **THE ONE PLACE A RANGE IS SPLIT — round 5 item 13.** `from..to` travels as a
     * single string everywhere else, because the toolbar's chip, its active test and *clear all*
     * all read one field. Here it becomes two query parameters, and either end may be empty:
     * `8..` is *at least eight hours* and `..8` is *at most*.
     */
    const [logFrom = '', logTo = ''] = logDateRange.split('..');
    if (logFrom !== '') params.set('from', logFrom);
    if (logTo !== '') params.set('to', logTo);
    const [hoursFrom = '', hoursTo = ''] = logHoursRange.split('..');
    if (hoursFrom !== '') params.set('hoursFrom', hoursFrom);
    if (hoursTo !== '') params.set('hoursTo', hoursTo);
    return params;
  }, [
    projectId,
    logSearch,
    logMemberId,
    logTeamId,
    logTaskId,
    logDateRange,
    logHoursRange,
    logPages.offset,
    logSort,
  ]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    /* ⚠️ The three non-fatal feeds start beside the project, not after it — see the list route. */
    const memberRequest = session.request(`/projects/${projectId}/members`).catch(() => null);
    const clientRequest = requestAll(session, '/clients').catch(() => null);
    const candidateRequest = requestAll(session, '/members').catch(() => null);
    const response = await session.request(`/projects/${projectId}`).catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const parsed = projectSchema.safeParse(await response.json());
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    setFailure(null);
    setProject(parsed.data);

    const memberResponse = await memberRequest;
    if (memberResponse?.ok === true) {
      const list = parseList(projectMemberSchema, await memberResponse.json());
      if (list !== null) setMembers(list);
    }

    /* ⚠️ For the edit dialog's client picker, which lives on this screen now — see
     * `onUpdateProject`. Failure is silent: it only thins one dropdown, and the screen's own
     * failure state is about the PROJECT. */
    const clientResponse = await clientRequest;
    if (clientResponse?.ok === true) {
      const list = parseList(clientSchema, await clientResponse.json());
      if (list !== null) setClients(list);
    }

    const candidateResponse = await candidateRequest;
    if (candidateResponse?.ok === true) {
      const list = parseList(workspaceMemberSchema, await candidateResponse.json());
      if (list !== null) setCandidates(list);
    }

    /**
     * ⚠️ **The three task loads run CONCURRENTLY**, because they do not depend on each other and
     * this is a tab somebody switches to expecting it to be there. Their failure is not fatal, for
     * the same reason as the pickers above: a caller without `task.view` should still see the
     * project rather than a full-page error about a tab they were not looking at.
     */
    const taskQuery = new URLSearchParams({ projectId });
    if (taskFilters.search.trim() !== '') taskQuery.set('search', taskFilters.search.trim());
    if (taskFilters.statusId !== '') taskQuery.set('statusId', taskFilters.statusId);
    if (taskFilters.priorityId !== '') taskQuery.set('priorityId', taskFilters.priorityId);
    /* ⚠️ Item 16's ranges and metafields, split into query parameters exactly as `tasks-route.tsx`
       does — the two screens are the same list and a filter that works on one and not the other is
       the *secretly different view* the project tab was rebuilt to stop being. */
    const taskRange = (value: string, lower: string, upper: string): void => {
      if (value === '') return;
      const [from = '', to = ''] = value.split('..');
      if (from !== '') taskQuery.set(lower, from);
      if (to !== '') taskQuery.set(upper, to);
    };
    taskRange(taskFilters.dueRange, 'dueAfter', 'dueBefore');
    taskRange(taskFilters.estimateRange, 'estimateMin', 'estimateMax');
    for (const [key, value] of Object.entries(taskFilters.metafields)) {
      if (value === '') continue;
      if (value.includes('..')) taskRange(value, `mf.${key}.from`, `mf.${key}.to`);
      else taskQuery.set(`mf.${key}`, value);
    }
    if (taskFilters.teamId !== '') taskQuery.set('teamId', taskFilters.teamId);
    if (taskFilters.assigneeMemberId !== '') {
      taskQuery.set('assigneeMemberId', taskFilters.assigneeMemberId);
    }
    setTrackerParams(taskQuery, taskFilters);
    /* ⚠️ The offset travels, or Next lands on the first page again. */
    if (taskPages.offset > 0) taskQuery.set('offset', String(taskPages.offset));
    const encodedSort = formatSort(taskSort);
    if (encodedSort !== undefined) taskQuery.set('sort', encodedSort);

    const [taskList, statusList, priorityList, logList, itemList] = await Promise.all([
      /* ⚠️ The offset travels, or Next lands on the first page again. */
      /**
       * ⚠️⚠️ **THE TOOLBAR'S FILTERS TRAVEL, AND THEY DID NOT.** The tab drew a search box, a
       * status picker, a priority picker and an assignee picker; `setTaskFilters` moved the state
       * and this query never read it, so all four did nothing. That is worse than not offering
       * them: somebody sets a status and reads the unchanged list as *"there are none"*.
       *
       * ⚠️ **`projectId` is not one of them.** It is the address — the narrowing this screen is —
       * and it is applied whatever the toolbar says. `tasks-screen.tsx`' `scopeProjectId` carries
       * the distinction.
       */
      session.request(`/tasks?${taskQuery.toString()}`).catch(() => null),
      session.request('/task-statuses').catch(() => null),
      session.request('/task-priorities').catch(() => null),
      session.request(`/worklogs?${logQuery.toString()}`).catch(() => null),
      /* ⚠️ C25 · **non-fatal, like every other list here.** A tenant without contracts is refused,
         which lands as an empty list — and an empty list is exactly how the dialog is told not to
         draw the picker at all. */
      session.request('/line-items').catch(() => null),
    ]);

    if (taskList?.ok === true) {
      const body: unknown = await taskList.json();
      const list = parseList(taskSchema, body);
      if (list !== null) setTasks(list);
      /* ⚠️ What the server says comes next — `usePages` turns it into the two buttons. */
      taskPages.settle(totalOf(body));
    }
    if (statusList?.ok === true) {
      const list = parseList(taskStatusSchema, await statusList.json());
      if (list !== null) setStatuses(list);
    }
    if (priorityList?.ok === true) {
      const list = parseList(taskPrioritySchema, await priorityList.json());
      if (list !== null) setPriorities(list);
    }
    if (logList?.ok === true) {
      const body: unknown = await logList.json();
      const list = parseList(worklogSchema, body);
      if (list !== null) {
        setWorklogs(list);
        /* 30 Oct 2026 · the filtered total turns into the pager — `usePages`. */
        logPages.settle(totalOf(body));
      }
    }
    if (itemList?.ok === true) {
      const list = parseList(lineItemSchema, await itemList.json());
      if (list !== null) setLineItems(list);
    }
    /* ⚠️ `taskPages.offset` is an INPUT to the query above, so pressing Next has to re-run this.
       `settle` is stable from `usePages` and deliberately absent — including it would rebuild the
       loader on every page and refetch for ever.

       ⚠️ `taskFilters` is in here too, so changing one re-reads. Without it the toolbar would move
       its own state and the list would sit still — which is precisely the bug this is fixing. */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above.
  }, [session, projectId, logQuery, taskPages.offset, taskFilters, taskSort]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /**
   *    * ┌───────────────────────────────────────────────────────────────────────┐
   *    * │ The project detail is live, and until now it only said so. R-19.
   *    * └───────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **`routes.tsx` DECLARED `live: DELIVERY_TOPICS` FOR THIS ROUTE AND NOTHING SUBSCRIBED.**
   * Reported against a Jira import: *"while the jira project sync is running, the project detail
   * page is not updating in real time while tasks are coming in, I have to F5 to see changes."*
   * The events were there — `apply.service.ts` audits every imported issue as `task.create` or
   * `task.update`, and `recordAudit` emits the `pg_notify` in the same statement — and `task` is in
   * `DELIVERY_TOPICS`. The hook was simply never called here.
   *
   * ⚠️⚠️ **THE GATE THAT EXISTS TO CATCH THIS PASSED**, because `live.test.ts` reads the route's
   * whole FOLDER and looks for one call: `ClientsRoute` at the top of this file has always had
   * one, so both project routes inherited its green tick. That resolution is deliberate — a screen,
   * its route file and its overlay legitimately share a subscription — and the cost of it is this
   * bug. The note is here rather than in the test because the finer gate would need to know which
   * component a call sits in, which text matching cannot answer.
   */
  useLiveInvalidation(PROJECT_DETAIL_TOPICS, reload);

  /**
   * ⚠️⚠️ **IT ANSWERS WHETHER IT WORKED, AND THAT ANSWER IS WHAT KEEPS A DIALOG OPEN.** It
   * returned `void`, so the task dialog closed on submit regardless — a refused create took the
   * form and everything typed into it away, and left a message behind with nothing to attach it to.
   * On the UAT server that was a `400` nobody could act on and a task retyped from memory. Same
   * shape as `tasks-route.tsx`'s, which has always returned a boolean.
   */
  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      setServerError(await messageFor(response, t));
      return false;
    }
    await reload();
    return true;
  };

  return (
    <>
      <ScreenState failure={failure} onRetry={() => void reload()}>
        {project === null ? null : (
          <ProjectScreen
            loading={loading}
            project={project}
            /* 26 Oct 2026 · the edit dialog's *Új ügyfél* too, for whoever may create one (R-46).
               The new client joins this page's list, so the picker can select it at once. */
            clientMetafields={clientMetafields}
            {...(permissions.has('client.manage')
              ? {
                  onCreateClient: async (draft: ClientDraft) => {
                    const created = await createClientFrom(session, t, draft);
                    if (created === null) return null;
                    setClients((current) => [...current, created]);
                    return created.id;
                  },
                }
              : {})}
            /* R-58 · every project at `workspace`; at `own`, only one the reader is a member of. */
            canSeeFinance={
              permissions.scopeOf('project.finance') === 'workspace' ||
              (permissions.scopeOf('project.finance') === 'own' &&
                members.some((member) => member.memberId === myMemberId))
            }
            members={members}
            metafields={metafields}
            candidates={candidates.map((member) => ({
              id: member.id,
              name: member.displayName,
              /* U-10 · the member picker refuses a non-tracker before the server does. */
              tracksTime: member.tracksTime,
            }))}
            /* 22 Oct 2026 · every project at `workspace`; at `own`, only one the reader is PM of —
               the server's rule (`project-manage.ts`), so no edit is offered that it would refuse. */
            canManage={
              permissions.scopeOf('project.manage') === 'workspace' ||
              (permissions.scopeOf('project.manage') === 'own' && project.pmMemberId === myMemberId)
            }
            worklogs={worklogs}
            logSearch={logSearch}
            logMemberId={logMemberId}
            logDateRange={logDateRange}
            logTaskId={logTaskId}
            logHoursRange={logHoursRange}
            logTaskChoices={logTaskChoices}
            logPage={{
              hasPrevious: logPages.hasPrevious,
              hasNext: logPages.hasNext,
              onPrevious: logPages.previous,
              onNext: logPages.next,
              index: logPages.index,
              count: logPages.count,
              total: logPages.total,
              onGoTo: logPages.goTo,
            }}
            logSort={logSort}
            onLogSortChange={(next) => {
              setLogSort(next);
              logPages.reset();
            }}
            /* 30 Oct 2026 · a filter changes the set, so it goes back to page one. */
            onLogSearch={(value) => {
              setLogSearch(value);
              logPages.reset();
            }}
            onLogMember={(value) => {
              setLogMemberId(value);
              logPages.reset();
            }}
            teams={teams}
            memberTeams={memberTeams}
            logTeamId={logTeamId}
            onLogTeam={(value) => {
              setLogTeamId(value);
              logPages.reset();
            }}
            onLogDateRange={(value) => {
              setLogDateRange(value);
              logPages.reset();
            }}
            onLogTask={(value) => {
              setLogTaskId(value);
              logPages.reset();
            }}
            onLogHoursRange={(value) => {
              setLogHoursRange(value);
              logPages.reset();
            }}
            tasks={tasks}
            statuses={statuses}
            priorities={priorities}
            canCreateTask={permissions.has('task.create')}
            /**
             * ⚠️⚠️ **NARROWED TO THIS PROJECT, HERE, RATHER THAN IN THE DIALOG.** A line item
             * reaches a project through its contract (`contracts.project_id` is `not null`), so one
             * from another project's contract would put these hours on the wrong client's budget —
             * refused server-side as `line_item_not_on_project`, which is a refusal nobody should
             * ever be shown because nothing should have offered it.
             */
            taskLineItems={lineItems
              /* ⚠️ C56 · a line item with no project has no tasks either, so it is not a
                 thing a task can be assigned to. The equality already excludes null. */
              /* ⚠️ U-7 · nor a licence, which takes no task (`line_item_takes_no_work`). */
              .flatMap((item) =>
                item.projectId === null ||
                item.projectId !== projectId ||
                !lineItemHasWork(item.type)
                  ? []
                  : [
                      {
                        id: item.id,
                        name: item.name,
                        contractCode: lineItemContractLabel(item),
                        projectId: item.projectId,
                        /* IP-3 · a closed one is drawn and unpickable — `line_item_closed`. */
                        closed: item.status === 'closed',
                      },
                    ],
              )}
            serverError={serverError}
            /**
             * ⚠️ **Item 30 · the four task views.** Every handler here is the project's own copy of
             * what `/tasks` does, and each is one `PATCH` — the endpoint's own shape.
             */
            canEditTask={permissions.has('task.update')}
            taskFilters={taskFilters}
            onTaskFiltersChange={(next) => {
              taskPages.reset();
              setTaskFilters(next);
            }}
            taskMetafields={taskMetafields}
            taskTrackerValues={taskTrackerValues}
            taskSort={taskSort}
            onTaskSortChange={(next) => {
              taskPages.reset();
              setTaskSort(next);
            }}
            onMoveTaskStatus={(task, statusId) => {
              void write(`/tasks/${task.id}`, {
                method: 'PATCH',
                body: JSON.stringify({ statusId, version: task.version }),
              });
            }}
            onRescheduleTask={(task, startsOn, dueOn) => {
              void write(`/tasks/${task.id}`, {
                method: 'PATCH',
                body: JSON.stringify({ startsOn, dueOn, version: task.version }),
              });
            }}
            onTaskGridEdit={(task, column, value) => {
              /* ⚠️ `grid-edit.ts`, not a second copy — the global screen resolves a status name to
                 an id with the same function, and a copy here is the drift it was extracted to
                 prevent. */
              void applyGridEdit(task, column, value, {
                statuses,
                members: members.map((member) => ({
                  id: member.id,
                  name: member.displayName,
                })),
                patch: (target, body) =>
                  write(`/tasks/${target.id}`, {
                    method: 'PATCH',
                    body: JSON.stringify({ ...body, version: target.version }),
                  }),
                onUnknown: setServerError,
                t,
              });
            }}
            onExportTasks={() => {
              /* ⚠️ This project's tasks, not the workspace's. */
              exporter.open('task', { projectId });
            }}
            onImportTasks={() => {
              importer.open('task');
            }}
            onCreateTask={async (draft: TaskDraft) =>
              write('/tasks', {
                method: 'POST',
                body: JSON.stringify({
                  title: draft.title.trim(),
                  description: draft.description.trim() === '' ? null : draft.description,
                  projectId,
                  statusId: draft.statusId === '' ? undefined : draft.statusId,
                  priorityId: draft.priorityId === '' ? undefined : draft.priorityId,
                  assigneeMemberId: draft.assigneeMemberId === '' ? null : draft.assigneeMemberId,
                  /* ⚠️ P2-32.2 · required: every task is created on a line item. */
                  lineItemId: draft.lineItemId,
                  /* ⚠️ An empty estimate is ABSENT, not `''` — the parser refuses an empty string
                   * by design, and sending one would turn "I did not estimate" into a refusal. */
                  ...(draft.estimateHours.trim() === ''
                    ? {}
                    : { estimateHours: draft.estimateHours.trim() }),
                  startsOn: draft.startsOn === '' ? null : draft.startsOn,
                  dueOn: draft.dueOn === '' ? null : draft.dueOn,
                }),
              })
            }
            /**
             * ⚠️ **The drawer, exactly as the task LIST does it.** `main.jsx` opens every task the
             * same way wherever it is clicked — `openTask(k)` sets `taskMode: 'drawer'` from the list,
             * the board, a comment and a notification alike. A project's Feladatok tab navigating to
             * the full page instead would mean the same row behaves differently depending on which
             * screen it is on.
             *
             * ⚠️ **The project stays mounted underneath**, which matters more here than on the list:
             * the tab, its filters and the surrounding dashboard figures all survive a peek.
             */
            onOpenTask={(task: Task) => {
              setOpenTaskId(task.id);
              setTaskMode(nextSurface('page'));
            }}
            /* ⚠️ Back to where the project was opened from — a client, a person, the dashboard;
               the list only for a project with no past (14 Oct). */
            onBack={() => {
              goBack('/projects');
            }}
            /**
             * ⚠⚠️ **THE DIALOG IS ON THE PROJECT NOW, AND THE OLD PROP WAS WHY EDITING LOOKED
             * MISSING.** It read *"editing lives on the LIST, where the dialog is"* and did
             * `go('/projects')` — so clicking Edit left the project, landed on the list, and
             * opened nothing. One screen fewer to maintain, and one feature nobody could reach.
             *
             * `project-screen.tsx` renders `ProjectDialog` itself, from the header kebab, which is
             * where `main.jsx`'s `pKebabMenu` opens it.
             */
            /* ⚠️ The Feladatok tab's Previous/Next — the same shape the tasks LIST uses. */
            taskPage={{
              hasPrevious: taskPages.hasPrevious,
              hasNext: taskPages.hasNext,
              onPrevious: taskPages.previous,
              onNext: taskPages.next,
              /* ⚠️ Item 20's `[<] [selector] [>] [total]`. The total counts what the filters
                 left, which is what `list.pageTotal` says in words. */
              index: taskPages.index,
              count: taskPages.count,
              total: taskPages.total,
              onGoTo: taskPages.goTo,
            }}
            onUpdateProject={async (draft) => {
              setServerError(null);
              const { keyPrefix: _prefix, ...rest } = projectBody(draft);
              const saved = await session
                .request(`/projects/${project.id}`, {
                  method: 'PUT',
                  body: JSON.stringify({ ...rest, version: project.version }),
                })
                .catch(() => null);
              if (saved?.ok !== true) {
                setServerError(
                  saved === null ? t('error.unavailable') : await messageFor(saved, t),
                );
                return false;
              }
              await reload();
              return true;
            }}
            clients={clients}
            projectRoles={projectRoles}
            /* ⚠️ `projectRoleId` is omitted when the picker was left on *default*, which is what
               makes `members.default_project_role_id` do anything. `null` would mean "no role". */
            onAddMember={async (memberId, validFrom, projectRoleId) => {
              const landed = await write(`/projects/${projectId}/members`, {
                method: 'POST',
                body: JSON.stringify({
                  memberId,
                  ...(validFrom === '' ? {} : { validFrom }),
                  ...(projectRoleId === undefined ? {} : { projectRoleId }),
                }),
              });
              if (landed) {
                const name =
                  candidates.find((candidate) => candidate.id === memberId)?.displayName ?? '';
                saved(t('project.people.toast.added', { name }));
              }
              return landed;
            }}
            onUpdateMember={(membership, validFrom, validUntil) => {
              void write(`/projects/${projectId}/members/${membership.id}`, {
                method: 'PUT',
                body: JSON.stringify({ validFrom, validUntil, version: membership.version }),
              });
            }}
            onRemoveMember={(membership) => {
              void write(`/projects/${projectId}/members/${membership.id}`, { method: 'DELETE' });
            }}
          />
        )}
      </ScreenState>

      {/* The drawer and the modal over the project. `full` leaves for the task's own address. */}
      <TaskOverlay
        taskId={openTaskId}
        mode={taskMode}
        canEdit={permissions.has('task.update')}
        canLogTime={permissions.has('worklog.create')}
        canEditWorklog={permissions.has('worklog.update')}
        canSchedule={permissions.has('task.schedule')}
        onModeChange={(next) => {
          if (next === 'full') {
            const open = tasks.find((candidate) => candidate.id === openTaskId);
            setOpenTaskId(null);
            if (open !== undefined) go(`/tasks/${open.key}`);
            return;
          }
          setTaskMode(next);
        }}
        onClose={() => {
          setOpenTaskId(null);
        }}
        onChanged={() => {
          void reload();
        }}
        onOpenTask={(id) => {
          setOpenTaskId(id);
        }}
      />

      {toaster}
      {/* ⚠️ The tab opened these and nothing drew them, so Import and Export did nothing. */}
      {exporter.dialog}
      {importer.dialog}
      <Toaster
        toasts={[...exporter.toasts, ...importer.toasts]}
        label={t('tasks.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={(id) => {
          exporter.dismiss(id);
          importer.dismiss(id);
        }}
      />
    </>
  );
}

/**
 * A refusal's `code` becomes a sentence.
 *
 * ⚠️ **Branching on `code`, never on `message`** — CLAUDE.md rule 5. The server's `message` is
 * English prose for a developer reading a log; the sentence a person reads is chosen here, in their
 * language, from the code.
 *
 * ⚠️ **The COUNT comes out of `details`, not out of the message.** `client_in_use` and
 * `holds_assigned_tasks` both carry a number the reader needs — the design system's voice rule is
 * to say it: *"Két függőség sérül"*, not "there is a problem".
 */
async function messageFor(response: Response, t: ReturnType<typeof useT>): Promise<string> {
  const envelope = (await response.json().catch(() => null)) as {
    error?: { code?: string; details?: { field?: string; message?: string }[] };
  } | null;

  const code = envelope?.error?.code ?? 'unavailable';
  const detail = envelope?.error?.details?.[0]?.message ?? '';

  if (code === 'duplicate_code') return t('error.duplicate_code', { value: detail });
  if (code === 'client_in_use') return t('error.client_in_use', { count: detail });
  if (code === 'holds_assigned_tasks') {
    return t('error.holds_assigned_tasks', { count: detail });
  }
  /* 21 Oct 2026 · ticking Internal project while billable line items stand in the way. */
  if (code === 'internal_project_billable') {
    return t('error.internal_project_billable', { count: detail });
  }
  /* U-10 · the NAME comes out of `details`, so the sentence says who. */
  if (code === 'member_not_tracking') return t('error.member_not_tracking', { name: detail });
  /* J-2 · a board drag on a task whose tracker status nothing maps. */
  if (code === 'task_status_unmapped') return t('error.task_status_unmapped', { status: detail });
  /* J-6 · a tag the tracker has no label for, on a synced task. */
  if (code === 'task_tag_not_in_tracker')
    return t('error.task_tag_not_in_tracker', { tag: detail });
  if (code === 'conflict') return t('error.conflict');
  if (code === 'not_found') return t('error.not_found');
  if (code === 'validation_failed') return t('error.validation_failed');
  return t('error.unavailable');
}
